import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/comics';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormSubmitted(true);
      setFormError('');
      return;
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d9otftiqs38sj6jc7fh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg.includes('spam') ? '提交失败，请稍后重试' : serverMsg);
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫之家_韩漫在线观看平台"
        description="了解韩漫之家——专业的韩漫在线阅读平台。品牌故事、用户口碑、联系方式一应俱全。免费下拉式漫画阅读，智能加载，流畅舒适。"
        keywords="韩漫之家,关于我们,韩漫网站,韩漫在线观看,免费漫画"
        canonical="https://hanmanzhijia.com/about"
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 韩漫之家',
          description: '了解韩漫之家品牌故事、用户口碑和联系方式。',
          url: 'https://hanmanzhijia.com/about',
        }}
      />

      <Navbar />
      <main className="pt-20 pb-16 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="mb-16">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
              关于韩漫之家
            </h1>
            <div className="bg-background-100 rounded-lg p-6 md:p-8">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">品牌介绍</h2>
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                <strong>韩漫之家</strong>成立于2024年，是国内领先的<strong>韩漫在线观看</strong>平台。
                我们专注于为华语地区的韩漫爱好者提供最优质的<strong>免费下拉式漫画</strong>阅读服务。
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                在韩漫之家，我们深知每一位读者的阅读体验至关重要。因此，我们自主研发了<strong>智能加载技术</strong>，
                让漫画的每一页都能无缝衔接，无需等待翻页加载，真正实现<strong>流畅舒适</strong>的阅读感受。
                无论是使用电脑还是手机，您都能享受到一致的高品质<strong>漫画韩漫网址</strong>阅读服务。
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed">
                我们的团队由一群热爱韩漫的编辑和技术人员组成，每日追踪韩国最新的漫画动态，
                确保平台上的内容及时更新，让读者能够第一时间看到最新话数。
                海量韩漫资源涵盖恋爱、热血、奇幻、悬疑、耽美等多种类型，满足不同读者的口味需求。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 bg-background-100 rounded-lg">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 text-xs font-bold">
                        {t.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-lg p-6">
                <h3 className="text-base font-semibold text-foreground-950 mb-4">联系方式</h3>
                <ul className="flex flex-col gap-3">
                  <li className="flex items-center gap-3 text-sm text-foreground-700">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary-50 rounded-lg">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    contact@hanmanzhijia.com
                  </li>
                  <li className="flex items-center gap-3 text-sm text-foreground-700">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary-50 rounded-lg">
                      <i className="ri-wechat-line text-primary-500"></i>
                    </div>
                    微信公众号：韩漫之家
                  </li>
                  <li className="flex items-center gap-3 text-sm text-foreground-700">
                    <div className="w-8 h-8 flex items-center justify-center bg-primary-50 rounded-lg">
                      <i className="ri-weibo-line text-primary-500"></i>
                    </div>
                    微博：@韩漫之家官方
                  </li>
                </ul>
              </div>

              <div className="bg-background-100 rounded-lg p-6">
                <h3 className="text-base font-semibold text-foreground-950 mb-4">给我们留言</h3>
                {formSubmitted ? (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-3">
                      <i className="ri-check-line text-primary-500 text-xl"></i>
                    </div>
                    <p className="text-sm font-medium text-foreground-950">感谢您的留言！</p>
                    <p className="text-xs text-foreground-600 mt-1">我们会尽快与您联系</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form action="https://readdy.ai/api/form/d9otftiqs38sj6jc7fh0" method="POST">
                    <div className="flex flex-col gap-3">
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                      />
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors duration-200"
                      />
                      <textarea
                        name="message"
                        placeholder="请输入您的留言..."
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors duration-200 resize-none"
                      ></textarea>
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                      >
                        发送留言
                      </button>
                      {formError && (
                        <p className="text-xs text-red-500 text-center">{formError}</p>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}